import React from 'react';
import Icon from '../../../components/AppIcon';

const BusinessInsights = ({ language, metrics, supplierId }) => {
  const content = {
    en: {
      title: 'Business Insights & Recommendations',
      subtitle: 'Actionable recommendations based on your performance data',
      insights: 'Key Insights',
      recommendations: 'Recommendations',
      noData: 'Insufficient data for insights'
    },
    zh: {
      title: '业务洞察与建议',
      subtitle: '基于您的性能数据的可操作建议',
      insights: '关键洞察',
      recommendations: '建议',
      noData: '数据不足，无法提供洞察'
    }
  };

  const t = content?.[language];

  const generateInsights = () => {
    if (!metrics) return [];

    const insights = [];
    const conversionRate = Number(metrics?.conversionRate || 0);
    const avgResponseHours = Number(metrics?.avgResponseHours || 0);
    const avgRating = Number(metrics?.avgRating || 0);
    const totalRevenue = Number(metrics?.totalRevenue || 0);

    // Conversion Rate Insights
    if (conversionRate > 60) {
      insights?.push({
        type: 'success',
        icon: 'TrendingUp',
        title: language === 'en' ? 'Excellent Conversion Rate' : '优秀的转化率',
        description: language === 'en' 
          ? `Your ${conversionRate?.toFixed(1)}% conversion rate is above industry average. Keep up the great work!`
          : `您的${conversionRate?.toFixed(1)}%转化率高于行业平均水平。继续保持！`
      });
    } else if (conversionRate < 40) {
      insights?.push({
        type: 'warning',
        icon: 'AlertTriangle',
        title: language === 'en' ? 'Low Conversion Rate' : '转化率较低',
        description: language === 'en'
          ? `Your ${conversionRate?.toFixed(1)}% conversion rate could be improved. Consider faster response times and competitive pricing.`
          : `您的${conversionRate?.toFixed(1)}%转化率可以改进。考虑更快的响应时间和竞争性定价。`
      });
    }

    // Response Time Insights
    if (avgResponseHours <= 12) {
      insights?.push({
        type: 'success',
        icon: 'Zap',
        title: language === 'en' ? 'Fast Response Time' : '快速响应',
        description: language === 'en'
          ? `Your ${avgResponseHours?.toFixed(1)} hour average response time is excellent. Buyers appreciate quick responses!`
          : `您的${avgResponseHours?.toFixed(1)}小时平均响应时间非常出色。买家欣赏快速响应！`
      });
    } else if (avgResponseHours > 24) {
      insights?.push({
        type: 'warning',
        icon: 'Clock',
        title: language === 'en' ? 'Slow Response Time' : '响应时间较慢',
        description: language === 'en'
          ? `Your ${avgResponseHours?.toFixed(1)} hour response time exceeds the 24-hour benchmark. Faster responses can improve conversion.`
          : `您的${avgResponseHours?.toFixed(1)}小时响应时间超过24小时基准。更快的响应可以提高转化率。`
      });
    }

    // Rating Insights
    if (avgRating >= 4.5) {
      insights?.push({
        type: 'success',
        icon: 'Star',
        title: language === 'en' ? 'Outstanding Customer Satisfaction' : '卓越的客户满意度',
        description: language === 'en'
          ? `Your ${avgRating?.toFixed(1)}/5 rating shows excellent customer satisfaction. This builds trust with new buyers.`
          : `您的${avgRating?.toFixed(1)}/5评分显示出色的客户满意度。这建立了与新买家的信任。`
      });
    } else if (avgRating < 4.0 && avgRating > 0) {
      insights?.push({
        type: 'warning',
        icon: 'AlertCircle',
        title: language === 'en' ? 'Customer Satisfaction Needs Attention' : '客户满意度需要关注',
        description: language === 'en'
          ? `Your ${avgRating?.toFixed(1)}/5 rating suggests room for improvement. Review feedback and address common concerns.`
          : `您的${avgRating?.toFixed(1)}/5评分表明有改进空间。查看反馈并解决常见问题。`
      });
    }

    // Revenue Insights
    if (totalRevenue > 100000) {
      insights?.push({
        type: 'info',
        icon: 'DollarSign',
        title: language === 'en' ? 'Strong Revenue Performance' : '强劲的收入表现',
        description: language === 'en'
          ? `You have generated $${totalRevenue?.toLocaleString()} in revenue. Consider expanding your product range.`
          : `您已产生$${totalRevenue?.toLocaleString()}的收入。考虑扩大您的产品系列。`
      });
    }

    return insights;
  };

  const generateRecommendations = () => {
    if (!metrics) return [];

    const recommendations = [];
    const conversionRate = Number(metrics?.conversionRate || 0);
    const avgResponseHours = Number(metrics?.avgResponseHours || 0);

    if (conversionRate < 50) {
      recommendations?.push(
        language === 'en' ?'Improve product descriptions and add more high-quality images' :'改进产品描述并添加更多高质量图片'
      );
      recommendations?.push(
        language === 'en' ?'Offer competitive pricing and flexible payment terms' :'提供竞争性定价和灵活的付款条款'
      );
    }

    if (avgResponseHours > 24) {
      recommendations?.push(
        language === 'en' ?'Set up email notifications to respond to inquiries faster' :'设置电子邮件通知以更快地回复询问'
      );
      recommendations?.push(
        language === 'en' ?'Consider hiring additional staff to handle customer communications' :'考虑雇佣额外员工处理客户沟通'
      );
    }

    recommendations?.push(
      language === 'en' ?'Regularly update your inventory and pricing information' :'定期更新库存和定价信息'
    );
    recommendations?.push(
      language === 'en' ?'Respond to all buyer reviews to show engagement' :'回复所有买家评价以显示参与度'
    );

    return recommendations;
  };

  const insights = generateInsights();
  const recommendations = generateRecommendations();

  const getInsightColor = (type) => {
    const colors = {
      success: 'bg-success/10 text-success',
      warning: 'bg-yellow-500/10 text-yellow-600',
      info: 'bg-primary/10 text-primary'
    };
    return colors?.[type] || colors?.info;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <Icon name="Lightbulb" size={20} className="text-primary" />
          <h3 className="text-lg font-heading font-semibold text-foreground">{t?.title}</h3>
        </div>
        <p className="text-sm text-muted-foreground">{t?.subtitle}</p>
      </div>

      {insights?.length === 0 ? (
        <div className="text-center py-12">
          <Icon name="BarChart3" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">{t?.noData}</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Key Insights */}
          <div>
            <h4 className="text-sm font-semibold text-foreground mb-3">{t?.insights}</h4>
            <div className="space-y-3">
              {insights?.map((insight, index) => (
                <div key={index} className="flex items-start gap-3 p-3 rounded-lg border border-border">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${getInsightColor(insight?.type)}`}>
                    <Icon name={insight?.icon} size={20} />
                  </div>
                  <div className="flex-1">
                    <h5 className="font-medium text-foreground mb-1">{insight?.title}</h5>
                    <p className="text-sm text-muted-foreground">{insight?.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recommendations */}
          <div>
            <h4 className="text-sm font-semibold text-foreground mb-3">{t?.recommendations}</h4>
            <ul className="space-y-2">
              {recommendations?.map((recommendation, index) => (
                <li key={index} className="flex items-start gap-2 text-sm text-foreground">
                  <Icon name="CheckCircle2" size={16} className="text-primary mt-0.5 flex-shrink-0" />
                  <span>{recommendation}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessInsights;